import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useFeaturesStore } from '../stores';
import { API_URL, formatCurrency, formatDate, getStatusColor, getCategoryColor, QUOTE_STATUSES, CATEGORIES } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Plus,
  Trash2,
  Package,
  CalendarDays,
  User,
  Calculator,
  FileBox,
  Edit2,
  PenLine,
  FileText,
  Loader2,
  Link2,
  Calendar as CalendarIcon,
} from 'lucide-react';

const QuoteDetailPage = () => {
  const { id } = useParams();
  const { token, can } = useAuthStore();
  const goldEnabled = useFeaturesStore((s) => s.gold);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [addFreeItemOpen, setAddFreeItemOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customPrice, setCustomPrice] = useState('');
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');

  // Free item state
  const [freeItemName, setFreeItemName] = useState('');
  const [freeItemDescription, setFreeItemDescription] = useState('');
  const [freeItemPrice, setFreeItemPrice] = useState('');
  const [freeItemQuantity, setFreeItemQuantity] = useState(1);

  const { data: quote, isLoading } = useQuery({
    queryKey: ['quote', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Quote not found');
      return res.json();
    },
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      return res.json();
    },
  });

  // --- Fonction pour ouvrir la modale avec les dates actuelles
  const openDateDialog = () => {
    if (quote) {
      // On convertit les dates ISO (YYYY-MM-DDTHH:mm:ss) en format input date (YYYY-MM-DD)
      setEditStartDate(quote.start_date ? quote.start_date.split('T')[0] : '');
      setEditEndDate(quote.end_date ? quote.end_date.split('T')[0] : '');
      setDateDialogOpen(true);
    }
  };

  // --- MODIFICATION : Fonction pour sauvegarder les dates ---
  const handleUpdateDates = () => {
    updateMutation.mutate({
      start_date: editStartDate || null,
      end_date: editEndDate || null
    });
    setDateDialogOpen(false);
  };

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/quotes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const errorData = await res.json();
        // Handle 409 Conflict - Stock availability error
        if (res.status === 409) {
          throw new Error(errorData.detail?.message || 'Stock insuffisant');
        }
        throw new Error(errorData.detail || 'Failed to update quote');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Devis mis à jour');
    },
    onError: (error) => {
      // Show detailed error for stock issues
      toast.error(error.message, { duration: 8000 });
    },
  });

  const handleAddProduct = () => {
    if (!selectedProduct) return;

    const product = products.find((p) => p.id === selectedProduct);
    if (!product) return;

    const finalPrice = customPrice ? parseFloat(customPrice) : product.price_ht;

    const newItem = {
      product_id: product.id,
      product_name: product.name,
      quantity: quantity,
      unit_price: finalPrice,
      catalog_price: product.price_ht,
      is_free_item: false,
      attributes: product.attributes,
    };

    const currentItems = quote?.items || [];
    const existingIndex = currentItems.findIndex((i) => i.product_id === product.id && i.unit_price === finalPrice);

    let updatedItems;
    if (existingIndex >= 0) {
      updatedItems = [...currentItems];
      updatedItems[existingIndex].quantity += quantity;
    } else {
      updatedItems = [...currentItems, newItem];
    }

    updateMutation.mutate({ items: updatedItems });
    setAddProductOpen(false);
    setSelectedProduct('');
    setQuantity(1);
    setCustomPrice('');
  };

  // Add free-form item (ligne libre)
  const handleAddFreeItem = () => {
    if (!freeItemName || !freeItemPrice) {
      toast.error('Nom et prix requis');
      return;
    }

    const newItem = {
      product_id: null,
      product_name: freeItemName,
      description: freeItemDescription,
      quantity: freeItemQuantity,
      unit_price: parseFloat(freeItemPrice),
      catalog_price: null,
      is_free_item: true,
      attributes: {},
    };

    const currentItems = quote?.items || [];
    updateMutation.mutate({ items: [...currentItems, newItem] });

    setAddFreeItemOpen(false);
    setFreeItemName('');
    setFreeItemDescription('');
    setFreeItemPrice('');
    setFreeItemQuantity(1);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = (quote?.items || []).filter((_, i) => i !== index);
    updateMutation.mutate({ items: updatedItems });
  };

  const handleUpdateQuantity = (index, newQty) => {
    if (newQty < 1) return;
    const updatedItems = (quote?.items || []).map((item, i) =>
      i === index ? { ...item, quantity: newQty } : item
    );
    updateMutation.mutate({ items: updatedItems });
  };

  // Update item price (custom price override)
  const handleUpdatePrice = (index, newPrice) => {
    const updatedItems = (quote?.items || []).map((item, i) =>
      i === index ? { ...item, unit_price: parseFloat(newPrice) } : item
    );
    updateMutation.mutate({ items: updatedItems });
    setEditingItemIndex(null);
  };

  const handleStatusChange = (status) => {
    // Check permission for validation
    if (['sent', 'accepted', 'signed'].includes(status) && !can('can_validate_quote')) {
      toast.error('Vous n\'avez pas la permission de valider ce devis');
      return;
    }
    updateMutation.mutate({ status });
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await fetch(`${API_URL}/quotes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis-${quote?.reference || id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Download packing list (bon de préparation)
  const handleDownloadPackingList = async () => {
    try {
      const res = await fetch(`${API_URL}/quotes/${id}/packing-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to generate packing list');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_preparation_${quote?.reference || id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Bon de préparation téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Convert signed quote to invoice
  const convertToInvoiceMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quote_id: id }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur lors de la conversion');
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Facture créée avec succès');
      navigate('/invoices');
    },
    onError: (err) => toast.error(err.message),
  });

  const handleCopyPortalLink = async () => {
    try {
      const res = await fetch(`${API_URL}/quotes/${id}/generate-portal-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur');
      const data = await res.json();
      const url = `${window.location.origin}/portal/quote/${data.access_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Lien client copié !', { description: url });
    } catch {
      toast.error('Erreur lors de la génération du lien');
    }
  };

  // Calculate totals
  const calculateDays = () => {
    if (!quote?.start_date || !quote?.end_date) return 1;
    const start = new Date(quote.start_date);
    const end = new Date(quote.end_date);
    return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
  };

  const days = calculateDays();
  const itemsTotal = (quote?.items || []).reduce(
    (sum, item) => sum + item.quantity * item.unit_price,
    0
  );
  const totalHT = itemsTotal * days;
  const totalTTC = totalHT * 1.2;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="text-center py-12">
        <p>Devis non trouvé</p>
        <Button onClick={() => navigate('/quotes')} className="mt-4">
          Retour aux devis
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quote-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/quotes')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold">
              {quote.reference}
            </h1>
            <p className="text-muted-foreground">
              {quote.client_name || 'Sans client'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={quote.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-40" data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QUOTE_STATUSES.map((s) => (
                <SelectItem
                  key={s.value}
                  value={s.value}
                  disabled={['sent', 'accepted', 'signed'].includes(s.value) && !can('can_validate_quote')}
                >
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> Devis
          </Button>
          <Button variant="outline" onClick={handleDownloadPackingList} data-testid="download-packing-btn">
            <FileBox className="w-4 h-4 mr-2" /> Bon Prépa
          </Button>
          {goldEnabled && (
            <Button variant="outline" onClick={handleCopyPortalLink} data-testid="copy-portal-link-btn">
              <Link2 className="w-4 h-4 mr-2" /> Lien Client
            </Button>
          )}
          {quote.status === 'signed' && (
            <Button
              variant="default"
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => {
                if (window.confirm('Convertir ce devis en facture ?')) {
                  convertToInvoiceMutation.mutate();
                }
              }}
              disabled={convertToInvoiceMutation.isPending}
              data-testid="convert-to-invoice-btn"
            >
              {convertToInvoiceMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Facturer
            </Button>
          )}
          {quote.status === 'invoiced' && (
            <Button variant="outline" onClick={() => navigate('/invoices')} data-testid="view-invoice-btn">
              <FileText className="w-4 h-4 mr-2" /> Voir la facture
            </Button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Client</p>
              <p className="font-medium">{quote.client_name || 'Non défini'}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative group"> {/* Ajout 'group' pour effet hover éventuel */}
          {/* Bouton Edit positionné en haut à droite de la card */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6 text-muted-foreground hover:text-primary"
            onClick={openDateDialog}
          >
            <Edit2 className="w-3 h-3" />
          </Button>

          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Période</p>
              <p className="font-medium">
                {quote.start_date && quote.end_date
                  ? `${formatDate(quote.start_date)} - ${formatDate(quote.end_date)} (${days}j)`
                  : 'Non définie'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total TTC</p>
              <p className="text-xl font-bold">{formatCurrency(totalTTC)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quote Texts - Intro, Notes, Footer */}
      <Tabs defaultValue="items" className="space-y-4">
        <TabsList>
          <TabsTrigger value="items">Produits & Services</TabsTrigger>
          <TabsTrigger value="details">Détails du devis</TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          {/* Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Produits & Services</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAddFreeItemOpen(true)} data-testid="add-free-item-btn">
                  <PenLine className="w-4 h-4 mr-2" /> Ligne libre
                </Button>
                <Button onClick={() => setAddProductOpen(true)} data-testid="add-item-btn">
                  <Plus className="w-4 h-4 mr-2" /> Produit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(quote.items || []).length > 0 ? (
                <div className="space-y-3">
                  {quote.items.map((item, index) => (
                    <div
                      key={`${item.product_id || 'free'}-${index}`}
                      className={`flex items-center gap-4 p-3 rounded-lg ${item.is_free_item ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-secondary/50'}`}
                      data-testid={`quote-item-${index}`}
                    >
                      <div className={`w-10 h-10 rounded-lg ${item.is_free_item ? 'bg-amber-500/20' : getCategoryColor('other')} flex items-center justify-center shrink-0`}>
                        {item.is_free_item ? <PenLine className="w-5 h-5 text-amber-600" /> : <Package className="w-5 h-5" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{item.product_name}</p>
                          {item.is_free_item && (
                            <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                              Ligne libre
                            </Badge>
                          )}
                          {item.catalog_price && item.unit_price !== item.catalog_price && (
                            <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30">
                              Prix modifié
                            </Badge>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-xs text-muted-foreground">{item.description}</p>
                        )}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {editingItemIndex === index ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                step="0.01"
                                className="w-20 h-6 text-xs"
                                defaultValue={item.unit_price}
                                onBlur={(e) => handleUpdatePrice(index, e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleUpdatePrice(index, e.target.value)}
                                autoFocus
                              />
                              <span>€ / unité</span>
                            </div>
                          ) : (
                            <span
                              className="cursor-pointer hover:text-foreground flex items-center gap-1"
                              onClick={() => setEditingItemIndex(index)}
                            >
                              {formatCurrency(item.unit_price)} / unité
                              <Edit2 className="w-3 h-3" />
                            </span>
                          )}
                          {item.catalog_price && item.unit_price !== item.catalog_price && (
                            <span className="text-xs line-through opacity-50">
                              (catalogue: {formatCurrency(item.catalog_price)})
                            </span>
                          )}
                        </div>
                        {item.attributes && Object.keys(item.attributes).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(item.attributes).slice(0, 2).map(([key, value]) => (
                              <Badge key={key} variant="outline" className="text-xs">
                                {key}: {String(value)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleUpdateQuantity(index, item.quantity - 1)}
                        >
                          -
                        </Button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleUpdateQuantity(index, item.quantity + 1)}
                        >
                          +
                        </Button>
                      </div>

                      <div className="text-right w-24">
                        <p className="font-bold">{formatCurrency(item.quantity * item.unit_price)}</p>
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => handleRemoveItem(index)}
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun produit ajouté</p>
                </div>
              )}

              {/* Totals */}
              {(quote.items || []).length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Sous-total / jour</span>
                      <span>{formatCurrency(itemsTotal)}</span>
                    </div>
                    {days > 1 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Durée</span>
                        <span>{days} jour(s)</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium">
                      <span>Total HT</span>
                      <span>{formatCurrency(totalHT)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>TVA (20%)</span>
                      <span>{formatCurrency(totalTTC - totalHT)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total TTC</span>
                      <span>{formatCurrency(totalTTC)}</span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Textes personnalisés</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="intro_text">Texte d'introduction</Label>
                <Textarea
                  id="intro_text"
                  placeholder="Suite à notre visite du..., nous avons le plaisir de vous soumettre notre proposition..."
                  defaultValue={quote.intro_text || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (quote.intro_text || '')) {
                      updateMutation.mutate({ intro_text: e.target.value });
                    }
                  }}
                  rows={3}
                  data-testid="intro-text-input"
                />
                <p className="text-xs text-muted-foreground">Apparaît avant le tableau des produits</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="footer_note">Remarques / Notes de bas de page</Label>
                <Textarea
                  id="footer_note"
                  placeholder="Livraison à l'étage sans ascenseur, installation comprise..."
                  defaultValue={quote.footer_note || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (quote.footer_note || '')) {
                      updateMutation.mutate({ footer_note: e.target.value });
                    }
                  }}
                  rows={3}
                  data-testid="footer-note-input"
                />
                <p className="text-xs text-muted-foreground">Apparaît après le tableau des produits</p>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="payment_terms">Conditions de règlement</Label>
                <Textarea
                  id="payment_terms"
                  placeholder="Acompte 30% à la commande, solde à la livraison."
                  defaultValue={quote.payment_terms || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (quote.payment_terms || '')) {
                      updateMutation.mutate({ payment_terms: e.target.value });
                    }
                  }}
                  rows={2}
                  data-testid="payment-terms-input"
                />
                <p className="text-xs text-muted-foreground">Laissez vide pour utiliser les conditions par défaut de l'entreprise</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes internes</Label>
                <Textarea
                  id="notes"
                  placeholder="Notes internes (non visibles sur le PDF)..."
                  defaultValue={quote.notes || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (quote.notes || '')) {
                      updateMutation.mutate({ notes: e.target.value });
                    }
                  }}
                  rows={2}
                />
              </div>

              {quote.validity_date && (
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-sm">
                    <span className="text-muted-foreground">Devis valable jusqu'au:</span>{' '}
                    <span className="font-medium">{formatDate(quote.validity_date)}</span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Product Dialog */}
      <Dialog open={addProductOpen} onOpenChange={setAddProductOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un produit</DialogTitle>
            <DialogDescription>
              Sélectionnez un produit du catalogue. Vous pouvez modifier le prix pour ce devis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Produit</Label>
              <Select value={selectedProduct} onValueChange={(v) => {
                setSelectedProduct(v);
                const p = products.find(p => p.id === v);
                if (p) setCustomPrice(p.price_ht.toString());
              }}>
                <SelectTrigger data-testid="select-product">
                  <SelectValue placeholder="Choisir un produit" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} - {formatCurrency(p.price_ht)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantité</Label>
                <Input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  data-testid="product-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label>Prix unitaire (€ HT)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  placeholder="Prix catalogue"
                  data-testid="custom-price"
                />
                {selectedProduct && customPrice && (
                  <p className="text-xs text-muted-foreground">
                    {parseFloat(customPrice) !== products.find(p => p.id === selectedProduct)?.price_ht
                      ? '⚠️ Prix différent du catalogue'
                      : '✓ Prix catalogue'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setAddProductOpen(false);
                setCustomPrice('');
              }}>
                Annuler
              </Button>
              <Button onClick={handleAddProduct} data-testid="confirm-add-product">
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Date Dialog */}
      <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Modifier la période</DialogTitle>
            <DialogDescription>
              Ajustez les dates de l'événement. Le montant total sera recalculé en fonction de la durée.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="start_date" className="text-right">
                Début
              </Label>
              <Input
                id="start_date"
                type="date"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="end_date" className="text-right">
                Fin
              </Label>
              <Input
                id="end_date"
                type="date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
                className="col-span-3"
              />
            </div>
          </div>
          {/* Affichage durée prévisionnelle */}
          {editStartDate && editEndDate && (
            <div className="text-sm text-center text-muted-foreground bg-muted/30 p-2 rounded">
              Nouvelle durée : {
                Math.max(1, Math.ceil((new Date(editEndDate) - new Date(editStartDate)) / (1000 * 60 * 60 * 24)) + 1)
              } jour(s)
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDateDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleUpdateDates}>Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Free Item Dialog */}
      <Dialog open={addFreeItemOpen} onOpenChange={setAddFreeItemOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une ligne libre</DialogTitle>
            <DialogDescription>
              Ajoutez un élément hors catalogue (frais de déplacement, service, etc.)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Désignation *</Label>
              <Input
                value={freeItemName}
                onChange={(e) => setFreeItemName(e.target.value)}
                placeholder="Ex: Frais de déplacement"
                data-testid="free-item-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Textarea
                value={freeItemDescription}
                onChange={(e) => setFreeItemDescription(e.target.value)}
                placeholder="Détails supplémentaires..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantité</Label>
                <Input
                  type="number"
                  min="1"
                  value={freeItemQuantity}
                  onChange={(e) => setFreeItemQuantity(parseInt(e.target.value) || 1)}
                />
              </div>
              <div className="space-y-2">
                <Label>Prix unitaire (€ HT) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={freeItemPrice}
                  onChange={(e) => setFreeItemPrice(e.target.value)}
                  placeholder="0.00"
                  data-testid="free-item-price"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setAddFreeItemOpen(false);
                setFreeItemName('');
                setFreeItemDescription('');
                setFreeItemPrice('');
                setFreeItemQuantity(1);
              }}>
                Annuler
              </Button>
              <Button onClick={handleAddFreeItem} data-testid="confirm-add-free-item">
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteDetailPage;
